import { CARD_TEXTS } from './texts-cards';
import type { MemoryRingProps } from './types';

/*
 * Кольцо «Память разговора N %» под полем сообщения (006, T112; US6-4; FR-043; contracts/ui.md §2; мокап Main.dc.html,
 * PhoneChat.dc.html): сколько места в памяти агента занял разговор (`agent_threads.mem_percent` из `efes.context`).
 * Пороги 75 и 90 %: до 75 — обычный цвет, от 75 — «внимание», от 90 — «заполнено». Смысл цвета дублирует число.
 * Полосу «Память разговора заполнена на 90 %» с «Начать новый разговор» показывает полоса состояний панели: порог —
 * `memoryLevel` и `MEMORY_FULL` отсюда. На телефоне подпись короче: «Память 24 %».
 */

export const MEMORY_WARN = 75;
export const MEMORY_FULL = 90;

export type MemoryLevel = 'normal' | 'warn' | 'full';

/** Уровень по порогам 75 и 90 % (FR-043). */
export function memoryLevel(percent: number): MemoryLevel {
  return percent >= MEMORY_FULL ? 'full' : percent >= MEMORY_WARN ? 'warn' : 'normal';
}

const STROKE: Record<MemoryLevel, string> = {
  normal: 'stroke-ink-2',
  warn: 'stroke-warn',
  full: 'stroke-bad',
};

/** Длина окружности r = 7 в рамке 20 × 20. */
const RING = 2 * Math.PI * 7;
const { label, text, say } = CARD_TEXTS;

export function MemoryRing({ percent }: MemoryRingProps) {
  if (percent === null || !Number.isFinite(percent)) return null;
  const p = Math.min(100, Math.max(0, Math.round(percent)));
  const level = memoryLevel(p);
  return (
    <span
      role="meter"
      aria-label={label.memory}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={p}
      aria-valuetext={`${p} %`}
      title={text.memoryHint}
      data-level={level}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 px-1.5 text-[12px] whitespace-nowrap text-ink-2 tabular-nums"
    >
      <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true" className="shrink-0">
        <circle cx="10" cy="10" r="7" strokeWidth="3" className="fill-none stroke-line" />
        <circle
          cx="10"
          cy="10"
          r="7"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${((RING * p) / 100).toFixed(1)} ${RING.toFixed(1)}`}
          transform="rotate(-90 10 10)"
          className={`fill-none ${STROKE[level]}`}
        />
      </svg>
      <span className="max-sm:hidden">{say.memory(p)}</span>
      <span className="sm:hidden">{say.memoryShort(p)}</span>
    </span>
  );
}
